import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Flex,
  HStack,
  VStack,
  SimpleGrid,
  useColorModeValue,
  Image
} from '@chakra-ui/react';

import aboutImage1 from '../assets/images/About_image_1.webp';
import aboutImage2 from '../assets/images/About_image_2.webp';
import aboutImage3 from '../assets/images/About_image_3.webp';
import aboutImage4 from '../assets/images/About_image_4.webp';

const aboutImages = [aboutImage1, aboutImage2, aboutImage3, aboutImage4];
const cards = [
  {
    title: 'Vision',
    description: 'Hacer que importar sea un proceso accesible, transparente y confiable para empresas y personas que necesitan traer productos desde el exterior.',
  },
  {
    title: 'Metodo',
    description: 'Analizamos tu necesidad, buscamos el proveedor adecuado y gestionamos todo el proceso logístico y aduanero con un enfoque claro y organizado.',
  },
  {
    title: 'Resultados',
    description: 'Importaciones exitosas, cargas seguras y clientes que confían en repetir porque saben que su inversión está protegida.',
  },
];

function AboutSection() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const cardBg = useColorModeValue('rgba(255, 255, 255, 0.8)', 'rgba(15, 23, 42, 0.6)');
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const cardTextColor = useColorModeValue('gray.600', 'gray.400');
  const accentColor = useColorModeValue('gray.900', 'white');

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === aboutImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000); // Cambia cada 3 segundos

    return () => clearInterval(interval);
  }, []);

  return (
    <Box id="nosotros" as="section" w="full">
      <Container maxW="1120px">
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          gap={10}
          align="center"
        >
          {/* Text Content - 60% */}
          <VStack align="start" spacing={6} flex="0 0 60%" maxW={{ lg: '60%' }}>
            <Heading 
              as="h2" 
              fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
              fontWeight={900}
            >
              Expertos en importaciones sin complicaciones.
            </Heading>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              color={subtitleColor}
              letterSpacing="wide"
              lineHeight="tall"
              pb={3}
              mb={2}
            >
              Somos un equipo especializado en importaciones internacionales, enfocado en resolver procesos complejos de forma clara, segura y eficiente. Acompañamos a nuestros clientes desde la idea inicial hasta la entrega final de su carga, encargándonos de cada detalle del proceso.
            </Text>
            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} w="full">
              {cards.map((card) => (
                <Box
                  key={card.title}
                  bg={cardBg}
                  borderRadius="xl"
                  p={5}
                  border="1px solid"
                  borderColor={cardBorder}
                >
                  <Heading as="h3" fontSize="lg" mb={2} color={accentColor} fontWeight="900">
                    {card.title}
                  </Heading>
                  <Text color={cardTextColor} fontSize="sm">
                    {card.description}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>

          {/* Image Gallery - 40% */}
          <Box
            flex={{ base: '0 0 auto', lg: '0 0 40%' }}
            maxW={{ lg: '40%' }}
            w="full"
            h={{ base: '300px', lg: '500px' }}
            bg={cardBg}
            borderRadius="2xl"
            border="1px solid"
            borderColor={cardBorder}
            position="relative"
            overflow="hidden"
          >
            {/* Images */}
            {aboutImages.map((img, index) => (
              <Box
                key={index}
                position="absolute"
                top={0}
                left={0}
                w="full"
                h="full"
                opacity={currentImageIndex === index ? 1 : 0}
                transition="opacity 0.5s ease-in-out"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Image
                  src={img}
                  alt={`About ${index + 1}`}
                  objectFit="cover"
                  w="full"
                  h="full"
                  loading="lazy"
                />
              </Box>
            ))}

            {/* Dots Indicator */}
            <HStack
              position="absolute"
              bottom={4}
              left="50%"
              transform="translateX(-50%)"
              spacing={2}
              zIndex={2}
            >
              {aboutImages.map((_, index) => (
                <Box
                  key={index}
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg={currentImageIndex === index ? 'brand.500' : 'whiteAlpha.400'}
                  cursor="pointer"
                  transition="all 0.3s"
                  onClick={() => setCurrentImageIndex(index)}
                  _hover={{ bg: currentImageIndex === index ? 'brand.500' : 'whiteAlpha.600' }}
                />
              ))}
            </HStack>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
}

export default AboutSection;
